import React, { useState, useEffect } from 'react';
import { useDogs } from '../hooks/useDogs';
import { normalizeText } from '../utils/dataUtils';

const DaycareForm = ({ 
    onSubmit, 
    onCancel, 
    loading, 
    formType = 'checkin', // 'checkin' or 'checkout'
    editingVisit = null,
    visits = [],
    initialData = null
}) => {
    const { dogs, loading: dogsLoading, loadDogs } = useDogs();
    
    const [formData, setFormData] = useState({
        dogId: '',
        visitId: '',
        notes: '',
        packageId: '' // For package assignment during checkin
    });
    
    const [errors, setErrors] = useState({});
    const [selectedDog, setSelectedDog] = useState(null);
    const [dogSearch, setDogSearch] = useState('');

    // Search dogs against the full dataset (not just the first page) as the user types
    useEffect(() => {
        if (formType !== 'checkin') return;
        const timer = setTimeout(() => {
            loadDogs({ search: dogSearch, limit: 50 });
        }, 300);
        return () => clearTimeout(timer);
    }, [dogSearch, loadDogs, formType]);

    // Pre-fill form when editing or with initial data
    useEffect(() => {
        if (editingVisit) {
            setFormData({
                dogId: editingVisit.dog?._id || '',
                visitId: editingVisit._id || '',
                notes: editingVisit.notes || '',
                packageId: editingVisit.package?._id || ''
            });
            
            if (editingVisit.dog) {
                setSelectedDog(editingVisit.dog);
            }
        } else if (initialData) {
            setFormData(prev => ({ ...prev, ...initialData }));
        }
    }, [editingVisit, initialData]);

    // Update selected dog when dog is selected
    useEffect(() => {
        if (formData.dogId && dogs.length > 0) {
            const dog = dogs.find(d => d._id === formData.dogId);
            setSelectedDog(dog);
        }
    }, [formData.dogId, dogs]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (formType === 'checkin' && !formData.dogId) {
            newErrors.dogId = 'Selecciona un perro';
        }

        if (formType === 'checkout' && !formData.visitId) {
            newErrors.visitId = 'Selecciona una visita para dar salida';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        onSubmit(formData);
    };

    const resetForm = () => {
        setFormData({
            dogId: '',
            visitId: '',
            notes: '',
            packageId: ''
        });
        setErrors({});
        setSelectedDog(null);
    };

    const handleCancel = () => {
        resetForm();
        onCancel();
    };

    const getTitle = () => {
        switch (formType) {
            case 'checkin':
                return editingVisit ? 'Editar Entrada' : 'Registrar Entrada';
            case 'checkout':
                return 'Registrar Salida';
            default:
                return 'Formulario Guardería';
        }
    };

    const getDescription = () => {
        switch (formType) {
            case 'checkin':
                return editingVisit ? 'Actualizar detalles de entrada.' : 'Registrar entrada de un perro a guardería.';
            case 'checkout':
                return 'Registrar salida de un perro de guardería.';
            default:
                return '';
        }
    };

    // Get available dogs for check-in (not already checked in today)
    const getAvailableDogsForCheckin = () => {
        const checkedInDogIds = visits
            .filter(visit => visit.checkInTime && !visit.checkOutTime)
            .map(visit => visit.dog?._id)
            .filter(Boolean);
        
        return dogs.filter(dog => !checkedInDogIds.includes(dog._id));
    };

    // Get available visits for check-out (checked in but not checked out)
    const getAvailableVisitsForCheckout = () => {
        return visits.filter(visit => visit.checkInTime && !visit.checkOutTime);
    };

    const getAvailableOptions = () => {
        if (formType === 'checkin') {
            return getAvailableDogsForCheckin().map(dog => ({
                value: dog._id,
                label: `${dog.name} - ${dog.owners?.map(o => o.name).join(', ')}`
            }));
        } else if (formType === 'checkout') {
            return getAvailableVisitsForCheckout().map(visit => {
                const checkInTime = new Date(visit.checkInTime).toLocaleTimeString('es-ES', {
                    hour: '2-digit',
                    minute: '2-digit'
                });
                return {
                    value: visit._id,
                    label: `${visit.dog?.name} - Entró a las ${checkInTime}`
                };
            });
        }
        return [];
    };

    const availableOptions = getAvailableOptions();

    return (
        <div className="daycare-form">
            <h3>{getTitle()}</h3>
            <p className="form-description">{getDescription()}</p>
            
            <form onSubmit={handleSubmit}>
                <div className="form-grid">
                    {formType === 'checkout' ? (
                        <div className="form-group full-width">
                            <label>Seleccionar perro para dar salida *</label>
                            <input
                                type="text"
                                className="dog-picker-search"
                                placeholder="Buscar perro..."
                                value={dogSearch}
                                onChange={e => setDogSearch(e.target.value)}
                            />
                            <div className="dog-picker-list">
                                {getAvailableVisitsForCheckout()
                                    .filter(v => normalizeText(v.dog?.name).includes(normalizeText(dogSearch)))
                                    .map(v => {
                                        const checkInTime = new Date(v.checkInTime).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
                                        const isSelected = formData.visitId === v._id;
                                        return (
                                            <button
                                                type="button"
                                                key={v._id}
                                                className={`dog-picker-card${isSelected ? ' dog-picker-card--selected' : ''}`}
                                                onClick={() => {
                                                    setFormData(prev => ({ ...prev, visitId: v._id }));
                                                    setErrors(prev => ({ ...prev, visitId: '' }));
                                                }}
                                            >
                                                <div className="dog-picker-avatar">
                                                    {v.dog?.pictureUrl || v.dog?.picture
                                                        ? <img src={v.dog.pictureUrl || v.dog.picture} alt={v.dog.name} />
                                                        : <span>{v.dog?.name?.[0]?.toUpperCase()}</span>
                                                    }
                                                </div>
                                                <div className="dog-picker-info">
                                                    <div className="dog-picker-name">{v.dog?.name}</div>
                                                    <div className="dog-picker-breed">Entró a las {checkInTime}</div>
                                                </div>
                                                {isSelected && <span className="dog-picker-check">✓</span>}
                                            </button>
                                        );
                                    })
                                }
                                {getAvailableVisitsForCheckout().filter(v => normalizeText(v.dog?.name).includes(normalizeText(dogSearch))).length === 0 && (
                                    <div className="dog-picker-empty">No hay perros listos para salida</div>
                                )}
                            </div>
                            {errors.visitId && <div className="error">{errors.visitId}</div>}
                        </div>
                    ) : (
                        <div className="form-group full-width">
                            <label>Perro *</label>
                            {editingVisit ? (
                                <div className="dog-picker-list dog-picker-list--disabled">
                                    {selectedDog && (
                                        <div className="dog-picker-card dog-picker-card--selected">
                                            <div className="dog-picker-avatar">
                                                {selectedDog.pictureUrl || selectedDog.picture
                                                    ? <img src={selectedDog.pictureUrl || selectedDog.picture} alt={selectedDog.name} />
                                                    : <span>{selectedDog.name?.[0]?.toUpperCase()}</span>
                                                }
                                            </div>
                                            <div className="dog-picker-info">
                                                <div className="dog-picker-name">{selectedDog.name}</div>
                                                <div className="dog-picker-breed">{selectedDog.breed}</div>
                                            </div>
                                            <span className="dog-picker-check">✓</span>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <>
                                    <input
                                        type="text"
                                        className="dog-picker-search"
                                        placeholder="Buscar perro..."
                                        value={dogSearch}
                                        onChange={e => setDogSearch(e.target.value)}
                                    />
                                    <div className="dog-picker-list">
                                        {getAvailableDogsForCheckin()
                                            .map(dog => {
                                                const isSelected = formData.dogId === dog._id;
                                                const ownerNames = dog.owners?.map(o => o.name).join(', ');
                                                const subtitle = [dog.breed, ownerNames].filter(Boolean).join(' · ');
                                                return (
                                                    <button
                                                        type="button"
                                                        key={dog._id}
                                                        className={`dog-picker-card${isSelected ? ' dog-picker-card--selected' : ''}`}
                                                        onClick={() => {
                                                            setFormData(prev => ({ ...prev, dogId: dog._id }));
                                                            setErrors(prev => ({ ...prev, dogId: '' }));
                                                        }}
                                                    >
                                                        <div className="dog-picker-avatar">
                                                            {dog.pictureUrl || dog.picture
                                                                ? <img src={dog.pictureUrl || dog.picture} alt={dog.name} />
                                                                : <span>{dog.name?.[0]?.toUpperCase()}</span>
                                                            }
                                                        </div>
                                                        <div className="dog-picker-info">
                                                            <div className="dog-picker-name">{dog.name}</div>
                                                            <div className="dog-picker-breed">{subtitle}</div>
                                                        </div>
                                                        {isSelected && <span className="dog-picker-check">✓</span>}
                                                    </button>
                                                );
                                            })
                                        }
                                        {getAvailableDogsForCheckin().length === 0 && (
                                            <div className="dog-picker-empty">No hay perros disponibles para entrada</div>
                                        )}
                                    </div>
                                </>
                            )}
                            {errors.dogId && <div className="error">{errors.dogId}</div>}
                        </div>
                    )}
                </div>

                <div className="form-group full-width">
                    <label>Notas</label>
                    <textarea
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        placeholder={formType === 'checkout'
                            ? "Notas sobre el día del perro..."
                            : "Instrucciones especiales o notas para el día..."
                        }
                        rows="3"
                    />
                </div>

                {/* Selected Dog Info */}
                {selectedDog && formType !== 'checkout' && (
                    <div className="selected-dog-info">
                        <h4>🐕 Información del perro</h4>

                        <div className="selected-dog-content">
                            {(selectedDog.picture || selectedDog.pictureUrl) && (
                                <div className="selected-dog-picture">
                                    <img
                                        src={selectedDog.pictureUrl || selectedDog.picture}
                                        alt={selectedDog.name || 'Perro'}
                                        className="selected-dog-img"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                        }}
                                    />
                                </div>
                            )}

                            <div className="dog-info-grid">
                                <p><strong>Nombre:</strong> {selectedDog.name}</p>
                                <p><strong>Raza:</strong> {selectedDog.breed}</p>
                                <p><strong>Edad:</strong> {selectedDog.age} años</p>
                                <p><strong>Propietario(s):</strong> {selectedDog.owners?.map(o => o.name).join(', ')}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Package Information for Check-in */}
                {formType === 'checkin' && selectedDog && (
                    <div className="package-section">
                        <h4>📦 Información de paquete</h4>
                        <p className="section-description">
                            El paquete de guardería de este perro se usará automáticamente si está disponible.
                        </p>
                        {/* Future: Show available packages and allow selection */}
                    </div>
                )}

                {availableOptions.length === 0 && (
                    <div className="no-options-warning">
                        <p>⚠️ No hay {formType === 'checkin' ? 'perros disponibles para entrada' : 'perros listos para salida'}.</p>
                        {formType === 'checkin' && <p>Todos los perros ya pueden estar registrados hoy.</p>}
                        {formType === 'checkout' && <p>No hay perros actualmente registrados.</p>}
                    </div>
                )}

                <div className="form-actions">
                    <button
                        type="button"
                        onClick={handleCancel}
                        className="btn-secondary"
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn-primary"
                        disabled={loading || dogsLoading || availableOptions.length === 0}
                    >
                        {loading ? 'Procesando...' : (
                            formType === 'checkin'
                                ? (editingVisit ? 'Actualizar Entrada' : 'Registrar Entrada')
                                : 'Registrar Salida'
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default DaycareForm;